import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { MaterialSymbol } from '../icons/MaterialSymbol';
import { TabbioMark } from '../common/TabbioMark';
import { CVDocumentPreview } from '../cv/CVDocumentPreview';
import { CvShareBottomSheet } from '../cv/CvShareBottomSheet';
import { CustomizeBottomSheet } from '../cv/CustomizeBottomSheet';
import { WebDialog } from '../common/WebDialog.web';
import { useLocale } from '../../hooks/useLocale';
import {
  buildPublicCvRouteUrl,
  buildPublicProfileUrl,
  getPublicSiteLabel,
} from '../../lib/public-web';
import { withCvShareRef } from '@tabbio/utils/analytics/cv-traffic';
import { useCVData, type CVProfile } from '../../lib/cv-mock-data';
import { revealPhotoInPresetWhenUploaded } from '../../lib/cv-photo-state';
import { WebSaveButton } from '../cv/forms/web-form-elements';
import { useOnboarding, type OnboardingStep } from './OnboardingProvider';
import { OnboardingConfetti } from './OnboardingConfetti';
import {
  resolvePreviewEditStep,
  resolvePreviewPhotoEditStep,
} from './preview-edit-targets';
import { OnboardingPreviewBar } from './OnboardingPreviewBar';
import { useOnboardingPreviewContinue } from './use-onboarding-preview-continue';

type SectionGroup = 'essentials' | 'boost';

interface ChecklistSection {
  key: string;
  group: SectionGroup;
  step: OnboardingStep;
  /** Material Symbols glyph name. */
  icon: string;
  title: string;
  subtitle: string;
  isComplete: (p: CVProfile) => boolean;
}

// Same section list + done predicates as the native StepComplete checklist.
const SECTIONS: ChecklistSection[] = [
  { key: 'contact', group: 'essentials', step: 'how-to-reach-you', icon: 'mail', title: 'Contact & social', subtitle: 'Your phone, email and links', isComplete: (p) => Boolean(p.contactEmail || p.phone || p.location) },
  { key: 'experience', group: 'essentials', step: 'experience', icon: 'work', title: 'Experience', subtitle: 'Your roles and employers', isComplete: (p) => p.experience.length > 0 },
  { key: 'education', group: 'essentials', step: 'education', icon: 'school', title: 'Education', subtitle: 'Schools and degrees', isComplete: (p) => p.education.length > 0 },
  { key: 'skills', group: 'essentials', step: 'skills', icon: 'analytics', title: 'Skills', subtitle: 'Add your skills', isComplete: (p) => p.skillGroups.some((g) => g.items.length > 0) },
  { key: 'summary', group: 'essentials', step: 'summary', icon: 'description', title: 'Professional summary', subtitle: 'Add a short intro', isComplete: (p) => Boolean(p.summary?.trim()) },
  // "Boost" sections are optional: not in the linear flow; "Add" jumps to their
  // standalone screen, which returns here on save.
  { key: 'highlights', group: 'boost', step: 'highlights', icon: 'trophy', title: 'Career highlights', subtitle: 'Pull your top wins', isComplete: (p) => p.highlights.length > 0 },
  { key: 'certifications', group: 'boost', step: 'certifications', icon: 'workspace_premium', title: 'Certifications', subtitle: 'Add your certifications', isComplete: (p) => p.certifications.length > 0 },
];

const GROUP_ORDER: SectionGroup[] = ['essentials', 'boost'];
const GROUP_LABEL: Record<SectionGroup, string> = {
  essentials: 'Essentials',
  boost: 'Boost your profile',
};
const isProgressSection = (section: ChecklistSection) => section.group !== 'boost';

export function StepComplete() {
  const { intent } = useOnboarding();
  if (intent === 'company') return <CompanyComplete />;
  return <SeekerComplete />;
}

// ─── Seeker: checklist ⇄ live-profile preview ─────────────────────────

function SeekerComplete() {
  // Preview state lives in the provider so it survives the
  // complete → who-sees-cv → back round-trip (issue #50).
  const { cvPreviewOpen } = useOnboarding();

  return (
    <div
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        backgroundColor: 'var(--bg)',
      }}
    >
      <OnboardingConfetti />
      {/* The CV stays on screen at all times on web; "Complete my CV" opens as
          a dialog over it rather than replacing it, so the user never loses
          sight of the document they are completing. Native keeps the
          full-screen checklist, where a modal that tall would not fit. */}
      <CvPreview />
      {cvPreviewOpen ? null : <CvChecklistDialog />}
    </div>
  );
}

// ─── Seeker: "Complete my CV" checklist (web: a dialog over the preview) ──

function CvChecklistDialog() {
  const { t } = useTranslation('auth');
  const { profile } = useCVData();
  const { goToStep, setCvPreviewOpen, onboardingError, clearError } = useOnboarding();

  const sections = useMemo(
    () => SECTIONS.map((s) => ({ ...s, done: s.isComplete(profile) })),
    [profile],
  );
  const percent = useMemo(() => {
    const progressSections = sections.filter(isProgressSection);
    const done = progressSections.filter((s) => s.done).length;
    return progressSections.length
      ? Math.round((done / progressSections.length) * 100)
      : 0;
  }, [sections]);

  // Closing the dialog IS "back to the preview": the provider flag is the
  // single source of truth for which of the two the user is looking at.
  const close = () => setCvPreviewOpen(true);

  return (
    <WebDialog
      title={t('onboarding.reviewCv.title', { defaultValue: 'Complete my CV' })}
      subtitle={t('onboarding.reviewCv.subtitle', {
        defaultValue: 'Tap any section to fill it in. Edit anytime.',
      })}
      onClose={close}
      maxWidth={560}
      // Inline in the header rather than floating above the first group, so the
      // ring reads as a property of the title (matches the native header row).
      headerLeading={<ProgressRing percent={percent} size={52} />}
      footer={
        <WebSaveButton
          label={t('onboarding.reviewCv.preview', {
            defaultValue: 'Preview my CV',
          })}
          onClick={close}
        />
      }
    >
      <div>
        {GROUP_ORDER.filter((group) =>
          sections.some((s) => s.group === group),
        ).map((group, renderedIndex) => {
          const groupSections = sections.filter((s) => s.group === group);
          return (
            <div key={group} style={{ marginTop: renderedIndex === 0 ? 0 : 24 }}>
              <div
                style={{
                  fontSize: 15,
                  fontWeight: 500,
                  color: 'var(--text-primary)',
                  paddingInline: 4,
                  marginBottom: 10,
                }}
              >
                {t(`onboarding.reviewCv.groups.${group}`, { defaultValue: GROUP_LABEL[group] })}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {groupSections.map((section) => (
                  <ChecklistRow
                    key={section.key}
                    icon={section.icon}
                    title={t(`onboarding.reviewCv.sections.${section.key}.title`, { defaultValue: section.title })}
                    subtitle={t(`onboarding.reviewCv.sections.${section.key}.subtitle`, { defaultValue: section.subtitle })}
                    done={section.done}
                    addLabel={t('onboarding.reviewCv.add', { defaultValue: 'Add' })}
                    editLabel={t('onboarding.reviewCv.edit', { defaultValue: 'Edit' })}
                    onOpen={() => void goToStep(section.step)}
                  />
                ))}
              </div>
            </div>
          );
        })}

        {onboardingError ? <ErrorLine text={onboardingError} onClick={clearError} /> : null}
      </div>
    </WebDialog>
  );
}

// ─── Seeker: live-profile preview (reached from the checklist) ────────

function CvPreview() {
  const previewContinue = useOnboardingPreviewContinue();
  const { t } = useTranslation('auth');
  const { isRTL } = useLocale();
  const { profile, updateCVConfig } = useCVData();
  const {
    profileData,
    setCvPreviewOpen,
    goToStep,
    onboardingError,
    clearError,
    cvPresetId,
    cvPresetLayout,
  } = useOnboarding();
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);

  // Mirrors the native seed: the preview renders from CVDataContext so the
  // Theme dialog's edits are visible, and the context is seeded from the
  // applied preset. Keyed on the preset ID so a manual theme tweak survives
  // re-entering the preview. Unconditional so clearing a preset also resets the
  // look; with no preset the value equals the provider default, so it no-ops.
  useEffect(() => {
    updateCVConfig(
      revealPhotoInPresetWhenUploaded(cvPresetLayout, profile.avatarUrl),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cvPresetId]);
  const copiedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
    },
    [],
  );

  // Public CV link shown in the preview header (e.g. `ahmed.tabbio.com`).
  const username = (profileData.username || '').trim();
  const siteLabel = getPublicSiteLabel() ?? 'tabbio.com';
  const shareUrl =
    buildPublicCvRouteUrl(username) ??
    (username ? `https://${username}.${siteLabel}` : null);

  // The CV avatar and the just-claimed username live in profileData (set at
  // profile-setup/this-is-you) but aren't synced into the CV profile, so
  // inject them here: without the username the document's Tabbio contact row
  // can't build `{handle}.tabbio.com` and diverges from the header pill.
  const previewProfile: CVProfile = useMemo(
    () => ({
      ...profile,
      avatarUrl: profile.avatarUrl || profileData.avatarUrl || '',
      username: (profileData.username || profile.username || '').trim(),
    }),
    [profile, profileData.avatarUrl, profileData.username],
  );

  const handleCopy = useCallback(async () => {
    if (!shareUrl || !navigator.clipboard) return;
    try {
      // Tagged so the owner's analytics can count visits from a copied link.
      await navigator.clipboard.writeText(withCvShareRef(shareUrl, 'copy'));
      setCopied(true);
      if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
      copiedTimerRef.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard unavailable (permissions/insecure context): ignore.
    }
  }, [shareUrl]);

  const handleOpenShareDialog = useCallback(() => {
    setShareDialogOpen(true);
  }, []);

  // Export lives on the /cv screen, not in the onboarding preview: with the
  // Download action gone from the dock there is no trigger here, so the format
  // menu and the inline export paywall (issue #289) went with it.

  const copyLabel = copied
    ? t('onboarding.complete.linkCopied', { defaultValue: 'Link copied' })
    : t('onboarding.complete.copyLink', { defaultValue: 'Copy link' });

  return (
    <>
      {/* Header chrome: back · public url (click to copy) · share */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          padding: '4px 0 12px',
        }}
      >
        <ChromeCircle
          onClick={() => setCvPreviewOpen(false)}
          label={t('onboarding.back', { defaultValue: 'Back' })}
        >
          <MaterialSymbol
            name="chevron_left"
            size={24}
            style={isRTL ? { transform: 'scaleX(-1)' } : undefined}
          />
        </ChromeCircle>

        <UrlPill
          onClick={() => void handleCopy()}
          label={copyLabel}
          disabled={!shareUrl}
        >
          <TabbioMark size={15} color="var(--text-primary)" />
          <span
            style={{
              fontSize: 14,
              fontWeight: 500,
              color: 'var(--text-primary)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {username || siteLabel}
          </span>
          {username ? (
            <span style={{ fontSize: 12, color: 'var(--text-tertiary)', whiteSpace: 'nowrap' }}>
              {`.${siteLabel}`}
            </span>
          ) : null}
          {shareUrl ? (
            <MaterialSymbol
              name={copied ? 'check' : 'content_copy'}
              size={15}
              style={{ color: 'var(--text-tertiary)', flexShrink: 0 }}
            />
          ) : null}
        </UrlPill>

        <ChromeCircle
          onClick={handleOpenShareDialog}
          label={t('onboarding.complete.share', { defaultValue: 'Share' })}
        >
          <MaterialSymbol name="share" size={19} />
        </ChromeCircle>
      </div>

      {/* CV document. The dock floats over it, so the tail padding keeps the
          last lines clear of the bar instead of a bordered strip taking space. */}
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        <div style={{ paddingBottom: 108 }}>
          {/* No cvConfig prop: renders from CVDataContext so the Theme dialog's
              live edits show up. Seeded from the applied preset above. */}
          <CVDocumentPreview
            readOnlyProjects
            profile={previewProfile}
            onEditPhoto={() => goToStep(resolvePreviewPhotoEditStep())}
            onEditSection={(section) => { if (section !== 'edit-projects') goToStep(resolvePreviewEditStep(section)); }}
          />
        </div>
      </div>

      {onboardingError ? (
        <div style={{ maxWidth: 560, marginInline: 'auto', width: '100%' }}>
          <ErrorLine text={onboardingError} onClick={clearError} noMargin />
        </div>
      ) : null}

      {/* Floating dock: Edit / Theme / "Continue" on one line. complete is
          not terminal: Continue goes straight to who-sees-cv (no pop-ups),
          then the survey and the paywall, which completes onboarding. */}
      <OnboardingPreviewBar
        onEdit={() => setCvPreviewOpen(false)}
        onTheme={() => setShowCustomize(true)}
        onContinue={previewContinue.onContinue}
        isBusy={previewContinue.busy}
      />

      {showCustomize ? (
        <CustomizeBottomSheet
          visible
          onClose={() => setShowCustomize(false)}
        />
      ) : null}

      {shareDialogOpen ? (
        <CvShareBottomSheet
          visible={shareDialogOpen}
          publicUrl={shareUrl}
          onClose={() => setShareDialogOpen(false)}
        />
      ) : null}

    </>
  );
}

// ─── Company: "You're all set!" ───────────────────────────────────────

function CompanyComplete() {
  const { t } = useTranslation('auth');
  const {
    companyData,
    postOnboardingRoute,
    completeOnboarding,
    isCompleting,
    onboardingError,
    clearError,
  } = useOnboarding();

  const companyUrl = buildPublicProfileUrl(companyData.slug || 'company', 'company');
  const destinationLabel =
    postOnboardingRoute === '/(employer)/jobs/create'
      ? t('onboarding.complete.goCreateJob', { defaultValue: 'Finish & create job' })
      : postOnboardingRoute === '/(employer)/candidates'
      ? t('onboarding.complete.goFindCandidates', { defaultValue: 'Finish & find candidates' })
      : t('onboarding.complete.goToTabbio', { defaultValue: 'Go to Tabbio' });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
        }}
      >
        <div style={{ maxWidth: 400, width: '100%', textAlign: 'center' }}>
          {/* Success icon */}
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              backgroundColor: 'rgba(0,0,0,0.06)',
              color: 'var(--text-primary)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginInline: 'auto',
              marginBottom: 24,
            }}
          >
            <MaterialSymbol name="check" size={28} />
          </div>

          <h1
            style={{
              fontFamily: "'Google Sans', sans-serif",
              fontSize: 32,
              fontWeight: 400,
              lineHeight: '40px',
              letterSpacing: '-0.01em',
              color: 'var(--text-primary)',
              margin: '0 0 8px',
            }}
          >
            {t('onboarding.complete.title', { defaultValue: "You're all set!" })}
          </h1>
          <p style={{ fontSize: 15, lineHeight: '22px', color: 'var(--text-secondary)', margin: '0 0 32px' }}>
            {t('onboarding.complete.companySubtitle', {
              defaultValue: 'Your company setup is ready. Choose your next hiring action.',
            })}
          </p>

          {/* Company link */}
          {companyUrl ? (
            <div
              style={{
                backgroundColor: 'rgba(0,0,0,0.04)',
                borderRadius: 20,
                padding: '18px 20px',
                marginBottom: 16,
                textAlign: 'start',
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-tertiary)', marginBottom: 4 }}>
                {t('onboarding.complete.companyLink', { defaultValue: 'Your career page:' })}
              </div>
              <div
                style={{
                  fontSize: 16,
                  fontWeight: 500,
                  color: 'var(--text-primary)',
                  wordBreak: 'break-word',
                }}
              >
                {companyUrl.replace(/^https?:\/\//, '')}
              </div>
            </div>
          ) : null}

          {onboardingError ? (
            <p
              style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 16, cursor: 'pointer' }}
              onClick={clearError}
            >
              {onboardingError}
            </p>
          ) : null}

          <WebSaveButton
            label={
              isCompleting
                ? t('onboarding.completing', { defaultValue: 'Setting up…' })
                : destinationLabel
            }
            onClick={completeOnboarding}
            loading={isCompleting}
          />
        </div>
      </div>
    </div>
  );
}

// ─── Pieces ───────────────────────────────────────────────────────────

/** Circular completion ring: web sibling of the native CvProgressRing. */
function ProgressRing({ percent, size = 64 }: { percent: number; size?: number }) {
  const pct = Math.max(0, Math.min(100, Math.round(percent)));
  const strokeWidth = 5;
  const r = (size - strokeWidth) / 2;
  const c = size / 2;
  const circumference = 2 * Math.PI * r;
  const offset = circumference * (1 - pct / 100);

  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} role="img" aria-label={`${pct}%`}>
        <circle
          cx={c}
          cy={c}
          r={r}
          stroke="rgba(0,0,0,0.08)"
          strokeWidth={strokeWidth}
          fill="none"
        />
        <circle
          cx={c}
          cy={c}
          r={r}
          stroke="var(--text-primary)"
          strokeWidth={strokeWidth}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          transform={`rotate(-90 ${c} ${c})`}
          style={{ transition: 'stroke-dashoffset 400ms ease' }}
        />
      </svg>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 17,
          fontWeight: 500,
          color: 'var(--text-primary)',
        }}
      >
        {pct}
        <span style={{ fontSize: 10, fontWeight: 600 }}>%</span>
      </div>
    </div>
  );
}

function ChecklistRow({
  icon,
  title,
  subtitle,
  done,
  addLabel,
  editLabel,
  onOpen,
}: {
  icon: string;
  title: string;
  subtitle: string;
  done: boolean;
  addLabel: string;
  editLabel: string;
  onOpen: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  const [addHovered, setAddHovered] = useState(false);

  const rowStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    width: '100%',
    minHeight: 60,
    padding: '8px 18px',
    borderRadius: 16,
    backgroundColor: done && hovered ? 'rgba(0,0,0,0.06)' : 'rgba(0,0,0,0.04)',
    transition: 'background-color 150ms',
    boxSizing: 'border-box',
  };

  const content = (
    <>
      <MaterialSymbol name={icon} size={20} style={{ color: 'var(--text-secondary)', flexShrink: 0 }} />
      <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3, textAlign: 'start' }}>
        <span
          style={{
            fontSize: 14,
            fontWeight: 500,
            color: 'var(--text-primary)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {title}
        </span>
        <span
          style={{
            fontSize: 12.5,
            color: 'var(--text-tertiary)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {subtitle}
        </span>
      </span>
    </>
  );

  if (done) {
    // Completed: check_circle stays as the "done" signal; the edit glyph +
    // clickable row let the user re-open the step to edit.
    return (
      <button
        type="button"
        onClick={onOpen}
        aria-label={`${editLabel}: ${title}`}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{ ...rowStyle, border: 'none', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'start' }}
      >
        {content}
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <MaterialSymbol
            name="edit"
            size={16}
            style={{ color: 'var(--text-tertiary)', opacity: hovered ? 1 : 0.75, transition: 'opacity 150ms' }}
          />
          <MaterialSymbol name="check_circle" size={20} style={{ color: 'var(--text-primary)' }} />
        </span>
      </button>
    );
  }

  return (
    <div style={rowStyle}>
      {content}
      <button
        type="button"
        onClick={onOpen}
        aria-label={`${addLabel}: ${title}`}
        onMouseEnter={() => setAddHovered(true)}
        onMouseLeave={() => setAddHovered(false)}
        style={{
          padding: '7px 16px',
          borderRadius: 9999,
          border: 'none',
          backgroundColor: addHovered ? 'rgba(0,0,0,0.08)' : 'rgba(0,0,0,0.06)',
          color: 'var(--text-secondary)',
          fontSize: 13,
          fontWeight: 500,
          fontFamily: 'inherit',
          cursor: 'pointer',
          flexShrink: 0,
          transition: 'background-color 150ms',
        }}
      >
        {addLabel}
      </button>
    </div>
  );
}

function ChromeCircle({
  onClick,
  label,
  children,
}: {
  onClick: () => void;
  label: string;
  children: ReactNode;
}) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        width: 44,
        height: 44,
        borderRadius: '50%',
        border: 'none',
        flexShrink: 0,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: hovered ? 'rgba(0,0,0,0.06)' : 'rgba(0,0,0,0.04)',
        color: 'var(--text-primary)',
        cursor: 'pointer',
        transition: 'background-color 150ms',
      }}
    >
      {children}
    </button>
  );
}

function UrlPill({
  onClick,
  label,
  disabled,
  children,
}: {
  onClick: () => void;
  label: string;
  disabled?: boolean;
  children: ReactNode;
}) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      aria-label={label}
      title={label}
      disabled={disabled}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        height: 44,
        maxWidth: '62%',
        minWidth: 0,
        paddingInline: 18,
        borderRadius: 9999,
        border: 'none',
        backgroundColor: hovered && !disabled ? 'rgba(0,0,0,0.06)' : 'rgba(0,0,0,0.04)',
        fontFamily: 'inherit',
        cursor: disabled ? 'default' : 'pointer',
        transition: 'background-color 150ms',
      }}
    >
      {children}
    </button>
  );
}

function ErrorLine({
  text,
  onClick,
  noMargin,
}: {
  text: string;
  onClick: () => void;
  noMargin?: boolean;
}) {
  return (
    <p
      onClick={onClick}
      style={{
        color: 'var(--text-secondary)',
        fontSize: 14,
        margin: noMargin ? 0 : '16px 0 0',
        textAlign: 'center',
        cursor: 'pointer',
      }}
    >
      {text}
    </p>
  );
}
